import * as Yup from "yup"

import useOTPRateLimit from "@hooks/useOTPRateLimit"

import { faIcon } from "@utils/faIcon"

import { Button } from "@atoms"

import { AlertBanner } from "@molecules"
import { EmailInput } from "@molecules/Form/Form"
import GenericForm from "@molecules/Form/GenericForm/GenericForm"
import { MutationSuccessFunctionParams } from "@molecules/Form/GenericForm/GenericForm.types"
import toast from "@molecules/Toast/Toast"

import { useComponentNavigation } from "@organisms/ComponentNavigation/ComponentNavigation.context"
import {
    EmailCodeRequestPayload,
    EmailCodeRequestResponse,
} from "@organisms/SecurityCheckDialog/SecurityCheckDialog.types"

import styles from "@pages/Auth/Auth.module.scss"
import useAuth from "@pages/Auth/Auth.store"

import { AUTH_ENDPOINTS } from "@endpoints/auth"

import { ACCOUNT_VERIFICATION_TEMPORARY_CODE_ID_HOLD } from "@constants/storage"

export type EmailSchema = {
    email_address: User["email"]
}

export const emailSchema: Yup.ObjectSchema<EmailSchema> = Yup.object().shape({
    email_address: Yup.string().required("Please enter an email address").email("Please enter a valid email address"),
})

export default function AuthEmailCodeForm() {
    const { preferredAuthMethod, setEmailData, emailData, setIsWaitingForCodeInput, isWaitingForCodeInput } = useAuth()

    const { navigate, currentComponentId } = useComponentNavigation()
    const isOnOTPSlide = currentComponentId?.endsWith("_otp")

    const isActive = preferredAuthMethod === "email_and_code"

    const {
        state: otpState,
        setState: setOtpState,
        isValid,
        isWithinCooldown,
    } = useOTPRateLimit(ACCOUNT_VERIFICATION_TEMPORARY_CODE_ID_HOLD)

    const onMutationSuccess = (args: MutationSuccessFunctionParams<EmailSchema, EmailCodeRequestResponse>) => {
        setEmailData({
            email: args.formValues.email_address,
            emailId: args.data.email_id,
        })

        // Store OTP state with timestamp for rate limiting
        setOtpState({
            codeVerificationId: args.data.email_id,
            sentAt: Date.now(),
            checkFor: "email",
            contactValue: args.formValues.email_address,
        })

        toast({
            variant: "default",
            type: "success",
            title: "Code sent",
        })

        setIsWaitingForCodeInput(true)
        navigate("email_and_code_otp")
    }

    const onRequestPasswordInsteadClick = () => {
        navigate("email_and_password")
    }

    // Handle click with rate limiting check - uses form value for comparison
    const createClickHandler = (currentEmail: string) => (event: React.MouseEvent<HTMLButtonElement>) => {
        const hasValidExistingCode = isValid("email", currentEmail)

        // If we have a valid code within the cooldown window, skip requesting and navigate directly
        if (hasValidExistingCode && otpState) {
            event.preventDefault()
            setEmailData({
                email: otpState.contactValue,
                emailId: otpState.codeVerificationId,
            })
            setIsWaitingForCodeInput(true)
            navigate("email_and_code_otp")
            return
        }

        // If waiting for code input but rate limit has expired, allow new request (shows toast)
        // If still within rate limit, just navigate to OTP screen
        if (isWaitingForCodeInput && isWithinCooldown) {
            event.preventDefault()
            navigate("email_and_code_otp")
        }
    }

    return (
        <GenericForm<EmailSchema, EmailCodeRequestResponse, Error, EmailCodeRequestPayload>
            formConfig={{
                defaultValues: {
                    email_address: emailData?.email || "",
                },
            }}
            formValues={emailData?.email ? { email_address: emailData.email } : undefined}
            formValidationSchema={emailSchema}
            mutationConfig={{
                method: "POST",
                endpoint: AUTH_ENDPOINTS.OTP.EMAIL_REQUEST,
                genericErrorMessage: "Could not request code.",
                disabled: isWithinCooldown,
            }}
            triggerFormReset={!isActive}
            onMutationSuccess={onMutationSuccess}
            className={styles.form}
        >
            {({ isPending, nonFieldError, form }) => {
                // Use form value for rate limiting comparison
                const currentEmail = form.watch("email_address") || ""

                return (
                    <>
                        {nonFieldError && (
                            <AlertBanner
                                type="banner"
                                message={nonFieldError}
                                status="error"
                                className={styles.topAlertBanner}
                            />
                        )}
                        <EmailInput<EmailSchema>
                            name="email_address"
                            label="Email address"
                            placeholder="Enter email address..."
                            isTabbable={isActive && !isOnOTPSlide}
                            size="md"
                        />
                        <div className={styles.formCTAs}>
                            <Button
                                type="submit"
                                size="lg"
                                variant="solid"
                                isFullWidth={true}
                                isTabbable={isActive && !isOnOTPSlide}
                                isProcessing={isPending}
                                onClick={createClickHandler(currentEmail)}
                                rightIcon={faIcon({ name: "arrow-right", style: "regular" })}
                            >
                                Get login code
                            </Button>
                            <Button
                                size="lg"
                                variant="ghost"
                                rightIcon={faIcon({ name: "arrow-right", style: "regular" })}
                                onClick={onRequestPasswordInsteadClick}
                                isTabbable={isActive && !isOnOTPSlide}
                                data-prevent-input-validation={true}
                                type="button"
                                isFullWidth={true}
                            >
                                Sign in with password
                            </Button>
                        </div>
                    </>
                )
            }}
        </GenericForm>
    )
}
